import { useState, useEffect } from 'react';
import { Volume2, VolumeX, BookOpen, Layers, Settings } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { profileStore } from '@/services/ProfileStore';
import { formatXP, formatCoins } from '@/lib/utils';

interface SettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCharacterSelect?: () => void;
}

export function SettingsModal({ open, onOpenChange, onCharacterSelect }: SettingsModalProps) {
  const [groupedMode, setGroupedMode] = useState(false);
  const [showExplanation, setShowExplanation] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [volume, setVolume] = useState(50);
  const [totalXP, setTotalXP] = useState(0);
  const [totalCoins, setTotalCoins] = useState(0);

  // Load settings on mount
  useEffect(() => {
    const settings = profileStore.getSettings();
    const profile = profileStore.getProfile();

    setGroupedMode(settings.groupedMode);
    setShowExplanation(settings.showExplanation);
    setAudioEnabled(settings.audioEnabled);
    setTotalXP(profile.totalXP);
    setTotalCoins(profile.totalCoins);
  }, [open]);

  const handleGroupedModeChange = (checked: boolean) => {
    setGroupedMode(checked);
    profileStore.updateSettings({ groupedMode: checked });
  };

  const handleShowExplanationChange = (checked: boolean) => {
    setShowExplanation(checked);
    profileStore.updateSettings({ showExplanation: checked });
  };

  const handleAudioEnabledChange = (checked: boolean) => {
    setAudioEnabled(checked);
    profileStore.updateSettings({ audioEnabled: checked });

    // Mute/unmute Phaser audio (will be handled by Phaser scene)
    const event = new CustomEvent('audio-toggle', { detail: { enabled: checked } });
    window.dispatchEvent(event);
  };

  const handleVolumeChange = (value: number[]) => {
    setVolume(value[0]);
    // Volume change would be handled by Phaser
    const event = new CustomEvent('audio-volume', { detail: { volume: value[0] / 100 } });
    window.dispatchEvent(event);
  };

  const handleResetProgress = () => {
    if (window.confirm('Are you sure you want to reset all progress? This cannot be undone.')) {
      profileStore.reset();
      setTotalXP(0);
      setTotalCoins(0);
    }
  };

  const handleExportData = () => {
    const data = {
      profile: profileStore.getProfile(),
      exportedAt: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `badgey-quest-progress-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-game-panel/95 border-2 border-game-panel-border max-w-md backdrop-blur-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-primary flex items-center gap-2">
            <Settings className="w-6 h-6" />
            Settings
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Configure your game preferences
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Game Settings */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider flex items-center gap-2">
              <span className="text-primary">⚙️</span>
              Game Options
            </h3>

            {/* Grouped Mode */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-border/40 hover:bg-secondary/50 transition-all">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-secondary/60 rounded-lg">
                  <Layers className="w-4 h-4 text-accent" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Grouped Mode
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Show root cards with children
                  </p>
                </div>
              </div>
              <Switch
                checked={groupedMode}
                onCheckedChange={handleGroupedModeChange}
              />
            </div>

            {/* Show Explanations */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-border/40 hover:bg-secondary/50 transition-all">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-secondary/60 rounded-lg">
                  <BookOpen className="w-4 h-4 text-accent" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Show Explanations
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Full remediation on wrong answers
                  </p>
                </div>
              </div>
              <Switch
                checked={showExplanation}
                onCheckedChange={handleShowExplanationChange}
              />
            </div>
          </div>

          <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />

          {/* Audio Settings */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider flex items-center gap-2">
              <span className="text-primary">🔊</span>
              Audio
            </h3>

            {/* Audio Enabled */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-border/40 hover:bg-secondary/50 transition-all">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-secondary/60 rounded-lg">
                  {audioEnabled ? (
                    <Volume2 className="w-4 h-4 text-accent" />
                  ) : (
                    <VolumeX className="w-4 h-4 text-muted-foreground" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Audio
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Background music and sound effects
                  </p>
                </div>
              </div>
              <Switch
                checked={audioEnabled}
                onCheckedChange={handleAudioEnabledChange}
              />
            </div>

            {/* Volume Slider */}
            {audioEnabled && (
              <div className="pl-3">
                <div className="flex items-center gap-4 p-3 rounded-lg bg-secondary/20 border border-border/30">
                  <span className="text-xs font-semibold text-muted-foreground w-10">
                    {volume}%
                  </span>
                  <Slider
                    value={[volume]}
                    onValueChange={handleVolumeChange}
                    max={100}
                    step={5}
                    className="flex-1"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />

          {/* Character Selection */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider flex items-center gap-2">
              <span className="text-primary">⚔️</span>
              Character
            </h3>
            <button
              onClick={onCharacterSelect}
              className="w-full px-4 py-3 text-sm font-medium text-foreground bg-secondary/40 hover:bg-secondary/70 rounded-lg border border-border/40 hover:border-accent/50 transition-all hover:scale-[1.02]"
            >
              Change Character
            </button>
          </div>

          <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />

          {/* Progress Display */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider flex items-center gap-2">
              <span className="text-primary">📊</span>
              Your Progress
            </h3>
            <div className="bg-gradient-to-br from-secondary/40 to-secondary/20 rounded-xl p-5 border border-border/40">
              <div className="grid grid-cols-2 gap-5">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Total XP</p>
                  <p className="text-2xl font-bold text-blue-400">
                    {formatXP(totalXP)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Total Coins</p>
                  <p className="text-2xl font-bold text-yellow-400">
                    {formatCoins(totalCoins)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />

          {/* Data Management */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider flex items-center gap-2">
              <span className="text-primary">💾</span>
              Data Management
            </h3>
            <div className="flex gap-2">
              <button
                onClick={handleExportData}
                className="flex-1 px-4 py-2.5 text-xs font-medium text-foreground bg-secondary/40 hover:bg-secondary/70 rounded-lg border border-border/40 hover:border-accent/50 transition-all hover:scale-[1.02]"
              >
                Export Progress
              </button>
              <button
                onClick={handleResetProgress}
                className="flex-1 px-4 py-2.5 text-xs font-medium text-game-wrong bg-game-wrong/10 hover:bg-game-wrong/20 rounded-lg border border-game-wrong/40 hover:border-game-wrong/60 transition-all hover:scale-[1.02]"
              >
                Reset Progress
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
